import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { Minus, Plus, Trash2, ShoppingBag, Store, Truck, Banknote, CreditCard, MapPin, Loader2, Smartphone } from 'lucide-react';
import { submitOrder } from '../mockData';

const COSTO_ENVIO = 50;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const OrderModal = ({ isOpen, onClose, cart, onUpdateCart, onRemoveFromCart, initialReferralCode = '' }) => {
  const [orderDetails, setOrderDetails] = useState({
    name: '',
    phone: '',
    address: '',
    notes: '',
    referral_code: initialReferralCode || ''
  });
  const [referralPreview, setReferralPreview] = useState(null); // {valid, referrer_name, reason}

  // Auto-validate the pre-filled referral code from a /r/:code short link
  useEffect(() => {
    if (!initialReferralCode) return;
    const code = initialReferralCode.trim().toUpperCase();
    setOrderDetails(prev => ({ ...prev, referral_code: code }));
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/referrals/validate/${encodeURIComponent(code)}`);
        const data = await r.json();
        setReferralPreview(data);
        if (data.valid) {
          toast.success(`🎁 ¡Bienvenido! Recomendado por ${data.referrer_name}`, { duration: 4000 });
        }
      } catch (e) { /* silent */ }
    })();
  }, [initialReferralCode]);
  const [tipoEntrega, setTipoEntrega] = useState('ventanilla');
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);
  const [myReferralInfo, setMyReferralInfo] = useState(null); // {referral_code, first_name, referral_count}
  const [lastOrderId, setLastOrderId] = useState(null);

  // Check if customer exists when phone number changes
  useEffect(() => {
    const checkCustomer = async () => {
      if (orderDetails.phone.length >= 10) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/customers/${orderDetails.phone}`);
          const data = await response.json();
          if (data.found && data.customer) {
            setCustomerFound(true);
            // Pre-fill data from existing customer
            setOrderDetails(prev => ({
              ...prev,
              name: prev.name || data.customer.name,
              address: prev.address || data.customer.last_address
            }));
            toast.info('¡Bienvenido de nuevo!', {
              description: 'Encontramos tu última dirección de entrega'
            });
          } else {
            setCustomerFound(false);
          }
        } catch (err) {
          console.error(err);
          // Error checking customer - silent fail
        }
        // Also check if the phone matches a registered VIP (to show "Share my code" card)
        try {
          const rr = await fetch(`${BACKEND_URL}/api/vip/referral-info/${orderDetails.phone}`);
          if (rr.ok) setMyReferralInfo(await rr.json());
          else setMyReferralInfo(null);
        } catch (err) { setMyReferralInfo(null); }
      }
    };
    
    const debounce = setTimeout(checkCustomer, 500);
    return () => clearTimeout(debounce);
  }, [orderDetails.phone]);

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const envio = tipoEntrega === 'domicilio' ? COSTO_ENVIO : 0;
    return (subtotal + envio).toFixed(2);
  };

  // Function to get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Tu navegador no soporta geolocalización');
      return;
    }

    setIsGettingLocation(true);
    toast.info('Buscando tu ubicación...', { duration: 2000 });
    
    // First check if we have permission
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'denied') {
          setIsGettingLocation(false);
          toast.error('Ubicación bloqueada', {
            description: 'Ve a Configuración > Chrome > Ubicación y actívala',
            duration: 5000
          });
          return;
        }
        // Continue with geolocation
        requestGeolocation();
      }).catch(() => {
        // Permissions API not supported, try anyway
        requestGeolocation();
      });
    } else {
      // Permissions API not supported (iOS), try directly
      requestGeolocation();
    }
  };

  const requestGeolocation = () => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Use Nominatim (OpenStreetMap) for reverse geocoding - FREE
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1&zoom=18`,
            {
              headers: {
                'Accept-Language': 'es',
                'User-Agent': 'MrCrunch28App/1.0'
              }
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            
            // Build a readable address
            const address = data.address || {};
            let fullAddress = '';
            
            if (address.road) fullAddress += address.road;
            if (address.house_number) fullAddress += ' ' + address.house_number;
            if (address.neighbourhood || address.suburb) fullAddress += ', ' + (address.neighbourhood || address.suburb);
            if (address.city || address.town || address.village || address.municipality) {
              fullAddress += ', ' + (address.city || address.town || address.village || address.municipality);
            }
            if (address.state) fullAddress += ', ' + address.state;
            
            if (fullAddress && fullAddress.length > 5) {
              setOrderDetails(prev => ({ ...prev, address: fullAddress }));
              toast.success('¡Ubicación detectada!', {
                description: 'Puedes editar la dirección si es necesario'
              });
            } else if (data.display_name) {
              // Use full display_name as fallback
              const shortAddress = data.display_name.split(',').slice(0, 4).join(',');
              setOrderDetails(prev => ({ ...prev, address: shortAddress }));
              toast.success('Ubicación detectada');
            } else {
              throw new Error('No address found');
            }
          } else {
            throw new Error('Geocoding service error');
          }
        } catch (err) {
          console.error(err);
          // Fallback: show Google Maps link with coordinates
          const mapsLink = `Ubicación: google.com/maps?q=${latitude},${longitude}`;
          setOrderDetails(prev => ({ 
            ...prev, 
            address: mapsLink
          }));
          toast.info('Ubicación detectada por GPS', {
            description: 'Agrega tu dirección completa manualmente'
          });
        } finally {
          setIsGettingLocation(false);
        }
      },
      (error) => {
        setIsGettingLocation(false);
        
        switch (error.code) {
          case 1: // PERMISSION_DENIED
            toast.error('Ubicación bloqueada por el navegador', {
              description: 'iPhone: Configuración > Chrome > Ubicación > Permitir. Luego recarga la página.',
              duration: 8000
            });
            break;
          case 2: // POSITION_UNAVAILABLE
            toast.error('Ubicación no disponible', {
              description: 'Activa el GPS de tu teléfono e intenta de nuevo'
            });
            break;
          case 3: // TIMEOUT
            toast.error('Tiempo agotado', {
              description: 'Intenta de nuevo en un lugar con mejor señal'
            });
            break;
          default:
            toast.error('Error al obtener ubicación', {
              description: 'Escribe tu dirección manualmente'
            });
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const subtotal = calculateSubtotal();
      const envio = tipoEntrega === 'domicilio' ? COSTO_ENVIO : 0;
      const totalFinal = subtotal + envio;
      
      const orderData = {
        name: orderDetails.name,
        phone: orderDetails.phone,
        address: tipoEntrega === 'domicilio' ? orderDetails.address : 'Recoger en ventanilla',
        notes: orderDetails.notes,
        items: cart,
        delivery_type: tipoEntrega,
        delivery_fee: envio,
        subtotal: subtotal,
        total: totalFinal,
        payment_method: metodoPago,
        referral_code: referralPreview?.valid ? (orderDetails.referral_code || '').trim().toUpperCase() : null,
      };
      
      // Save order to backend
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        throw new Error('Error al guardar el pedido');
      }

      const savedOrder = await response.json();
      
      // Build message using simple array and join - most compatible method
      const lineas = [];
      
      lineas.push('NUEVO PEDIDO - Mr. Crunch 28');
      lineas.push('');
      lineas.push('Cliente: ' + orderDetails.name);
      lineas.push('Telefono: ' + orderDetails.phone);
      
      // Tipo de entrega
      if (tipoEntrega === 'domicilio') {
        lineas.push('Entrega: A DOMICILIO');
        lineas.push('Direccion: ' + orderDetails.address);
      } else {
        lineas.push('Entrega: RECOGER EN VENTANILLA');
      }
      lineas.push('');
      lineas.push('--- PEDIDO ---');
      lineas.push('');
      
      let subtotalProductos = 0;
      for (let i = 0; i < cart.length; i++) {
        const prod = cart[i];
        const nombre = String(prod.name || 'Producto');
        const precio = Number(prod.price) || 0;
        const cantidad = Number(prod.quantity) || 1;
        const subtotalItem = precio * cantidad;
        subtotalProductos += subtotalItem;
        
        lineas.push((i + 1) + '. ' + nombre);
        lineas.push('   Cantidad: ' + cantidad);
        lineas.push('   Precio: $' + precio.toFixed(2));
        lineas.push('   Subtotal: $' + subtotalItem.toFixed(2));
        lineas.push('');
      }
      
      lineas.push('==================');
      lineas.push('Subtotal: $' + subtotalProductos.toFixed(2));
      if (tipoEntrega === 'domicilio') {
        lineas.push('Envio a domicilio: $' + COSTO_ENVIO.toFixed(2));
      }
      lineas.push('TOTAL: $' + totalFinal.toFixed(2) + ' MXN');
      lineas.push('==================');
      lineas.push('');
      const pagoLabel = metodoPago === 'efectivo' ? 'EFECTIVO' : metodoPago === 'transferencia' ? 'TRANSFERENCIA' : 'TARJETA';
      lineas.push('PAGO: ' + pagoLabel);
      lineas.push('');
      
      if (orderDetails.notes && orderDetails.notes.trim()) {
        lineas.push('NOTAS: ' + orderDetails.notes);
        lineas.push('');
      }
      
      lineas.push('ID: ' + savedOrder.id);
      lineas.push('');
      lineas.push('Ver estado: food-order-crunch.emergent.host/tracking/' + savedOrder.id);
      
      // Join with newlines and encode once at the end
      const mensajeFinal = lineas.join('\n');
      const whatsappUrl = 'https://wa.me/528771228589?text=' + encodeURIComponent(mensajeFinal);
      
      window.open(whatsappUrl, '_blank');
      
      // Store last order ID for tracking
      setLastOrderId(savedOrder.id);
      
      toast.success('¡Pedido guardado!', {
        description: 'Se abrió WhatsApp para confirmar tu pedido'
      });
      
      // Clear cart and close
      cart.forEach(item => onRemoveFromCart(item.id));
      setOrderDetails({ name: '', phone: '', address: '', notes: '' });
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Error al procesar el pedido', {
        description: 'Por favor inténtalo de nuevo.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setOrderDetails({
      ...orderDetails,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] bg-gradient-to-br from-gray-900 to-black border-2 border-orange-500/30">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">
            <ShoppingBag className="inline-block mr-2 h-6 w-6 text-orange-500" />
            Tu Pedido
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Revisa tu orden y completa los detalles de entrega
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          {/* Cart Items */}
          {cart.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">Tu carrito está vacío</p>
            </div>
          ) : (
            <div className="space-y-4 pr-4">
              {cart.map((item) => (
                <div key={item.id} className="flex items-center space-x-4 bg-gradient-to-r from-red-950/30 to-orange-950/30 border border-gray-700 rounded-lg p-4">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-lg border-2 border-orange-500/30"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-white">{item.name}</h4>
                    <p className="text-sm text-gray-400">${item.price}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8"
                      onClick={() => onUpdateCart(item.id, item.quantity - 1)}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-semibold">{item.quantity}</span>
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8"
                      onClick={() => onUpdateCart(item.id, item.quantity + 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => onRemoveFromCart(item.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              <Separator className="my-4 bg-gray-700" />

              {/* Tipo de Entrega */}
              <div>
                <Label className="text-gray-300 text-lg mb-3 block">¿Cómo deseas recibir tu pedido?</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setTipoEntrega('ventanilla')}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      tipoEntrega === 'ventanilla'
                        ? 'border-orange-500 bg-orange-500/20 text-orange-500'
                        : 'border-gray-700 bg-black/30 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <Store className="h-8 w-8" />
                    <span className="font-semibold">Recoger en Ventanilla</span>
                    <span className="text-sm">Sin costo adicional</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTipoEntrega('domicilio')}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      tipoEntrega === 'domicilio'
                        ? 'border-orange-500 bg-orange-500/20 text-orange-500'
                        : 'border-gray-700 bg-black/30 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <Truck className="h-8 w-8" />
                    <span className="font-semibold">Envío a Domicilio</span>
                    <span className="text-sm">+ $50.00 MXN</span>
                  </button>
                </div>
              </div>

              <Separator className="my-4 bg-gray-700" />

              {/* Método de Pago */}
              <div>
                <Label className="text-gray-300 text-lg mb-3 block">¿Cómo deseas pagar?</Label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setMetodoPago('efectivo')}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      metodoPago === 'efectivo'
                        ? 'border-green-500 bg-green-500/20 text-green-500'
                        : 'border-gray-700 bg-black/30 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <Banknote className="h-7 w-7" />
                    <span className="font-semibold text-sm">Efectivo</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMetodoPago('transferencia')}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      metodoPago === 'transferencia'
                        ? 'border-blue-500 bg-blue-500/20 text-blue-500'
                        : 'border-gray-700 bg-black/30 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <Smartphone className="h-7 w-7" />
                    <span className="font-semibold text-sm">Transferencia</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMetodoPago('tarjeta')}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      metodoPago === 'tarjeta'
                        ? 'border-purple-500 bg-purple-500/20 text-purple-500'
                        : 'border-gray-700 bg-black/30 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <CreditCard className="h-7 w-7" />
                    <span className="font-semibold text-sm">Tarjeta</span>
                  </button>
                </div>
              </div>

              <Separator className="my-4 bg-gray-700" />

              {/* Total con desglose */}
              <div className="space-y-2">
                <div className="flex justify-between items-center text-lg">
                  <span className="text-gray-400">Subtotal:</span>
                  <span className="text-white">${calculateSubtotal().toFixed(2)}</span>
                </div>
                {tipoEntrega === 'domicilio' && (
                  <div className="flex justify-between items-center text-lg">
                    <span className="text-gray-400">Envío a domicilio:</span>
                    <span className="text-white">+ ${COSTO_ENVIO.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-xl font-bold pt-2 border-t border-gray-700">
                  <span className="text-white">Total:</span>
                  <span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">${calculateTotal()}</span>
                </div>
              </div>

              <Separator className="my-4 bg-gray-700" />

              {/* Order Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="customer-name" className="text-gray-300">Nombre Completo</Label>
                  <Input
                    id="customer-name"
                    name="name"
                    value={orderDetails.name}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                    required
                    className="mt-2 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                  />
                </div>

                <div>
                  <Label htmlFor="customer-phone" className="text-gray-300">Teléfono</Label>
                  <Input
                    id="customer-phone"
                    name="phone"
                    type="tel"
                    value={orderDetails.phone}
                    onChange={handleChange}
                    placeholder="877 123 4567"
                    required
                    className="mt-2 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                  />
                </div>

                {tipoEntrega === 'domicilio' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="customer-address" className="text-gray-300">Dirección de Entrega</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={getCurrentLocation}
                        disabled={isGettingLocation}
                        className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                      >
                        {isGettingLocation ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Buscando...
                          </>
                        ) : (
                          <>
                            <MapPin className="h-4 w-4 mr-1" />
                            Usar mi ubicación
                          </>
                        )}
                      </Button>
                    </div>
                    <Textarea
                      id="customer-address"
                      name="address"
                      value={orderDetails.address}
                      onChange={handleChange}
                      placeholder="Calle, número, colonia, ciudad"
                      rows={3}
                      required
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                    />
                  </div>
                )}

                {/* "Share my code" card — appears when phone matches a registered VIP */}
                {myReferralInfo?.referral_code && (
                  <div className="bg-gradient-to-br from-pink-900/30 to-fuchsia-900/20 border-2 border-pink-500/50 rounded-xl p-4">
                    <p className="text-pink-300 font-bold text-sm mb-1 flex items-center gap-1">🎁 ¡Invita a tus amigos y gana!</p>
                    <p className="text-gray-300 text-xs mb-3">
                      Comparte tu código. Cada vez que un amigo lo use y su pedido sea entregado, recibes el 2% en puntos.
                    </p>
                    <div className="bg-black/50 border border-pink-500/40 rounded-lg px-3 py-2 mb-3 flex items-center justify-between">
                      <span className="text-pink-200 font-mono text-2xl font-black tracking-wider">{myReferralInfo.referral_code}</span>
                      <span className="text-xs text-gray-400">{myReferralInfo.referral_count} referidos</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        onClick={() => {
                          const shortLink = `${window.location.origin}/r/${myReferralInfo.referral_code}`;
                          const msg = `🍗 ¡Te invito a probar Mr Crunch 28! Usa mi código *${myReferralInfo.referral_code}* en tu primera compra y recibe un burrito GRATIS al realizar una compra mínima de $150. ¡Todo está delicioso! 😋\n\n📲 ${shortLink}\n📞 WhatsApp: 877 122 8589`;
                          navigator.clipboard.writeText(msg);
                          toast.success(`📋 Mensaje con link corto copiado`);
                        }}
                        className="bg-gray-700 hover:bg-gray-600 text-white"
                        data-testid="btn-share-code-copy"
                      >
                        📋 Copiar mensaje
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          const shortLink = `${window.location.origin}/r/${myReferralInfo.referral_code}`;
                          const msg = `🍗 ¡Te invito a probar Mr Crunch 28! Usa mi código *${myReferralInfo.referral_code}* en tu primera compra y recibe un burrito GRATIS al realizar una compra mínima de $150. ¡Todo está delicioso! 😋\n\n📲 ${shortLink}\n📞 WhatsApp: 877 122 8589`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                        }}
                        className="bg-green-600 hover:bg-green-500 text-white"
                        data-testid="btn-share-code-wa"
                      >
                        💬 Invitar por WhatsApp
                      </Button>
                    </div>
                  </div>
                )}

                {/* Referral code — optional */}
                <div>
                  <Label className="text-gray-300 flex items-center gap-1">🎁 ¿Alguien te recomendó? <span className="text-gray-500 text-xs">(opcional)</span></Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      name="referral_code"
                      value={orderDetails.referral_code}
                      onChange={(e) => { setOrderDetails({...orderDetails, referral_code: e.target.value.toUpperCase()}); setReferralPreview(null); }}
                      placeholder="Ej: JOSE482"
                      className="flex-1 bg-black/50 border-gray-700 text-white uppercase font-mono placeholder:text-gray-500 focus:border-pink-500"
                      data-testid="input-order-referral"
                    />
                    <Button
                      type="button"
                      disabled={!orderDetails.referral_code}
                      onClick={async () => {
                        const code = (orderDetails.referral_code || '').trim().toUpperCase();
                        if (!code) return;
                        try {
                          const r = await fetch(`${BACKEND_URL}/api/referrals/validate/${encodeURIComponent(code)}`);
                          const data = await r.json();
                          setReferralPreview(data);
                          if (data.valid) toast.success(`¡Código válido! Recomendado por ${data.referrer_name}`);
                          else toast.error(data.reason === 'not_found' ? 'No encontramos este código' : data.reason === 'blocked' ? 'Código bloqueado' : 'Código inválido');
                        } catch (e) { toast.error('Error validando'); }
                      }}
                      className="bg-pink-600 hover:bg-pink-500 disabled:opacity-40"
                      data-testid="btn-order-validate-ref"
                    >
                      Validar
                    </Button>
                  </div>
                  {referralPreview?.valid && (
                    <p className="text-green-400 text-xs mt-1">✅ Código válido — recomendado por {referralPreview.referrer_name}</p>
                  )}
                  {referralPreview && !referralPreview.valid && (
                    <p className="text-red-400 text-xs mt-1">❌ {referralPreview.reason === 'not_found' ? 'No encontramos este código' : referralPreview.reason === 'blocked' ? 'Código bloqueado' : 'Código inválido'}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="customer-notes" className="text-gray-300">Notas Adicionales (opcional)</Label>
                  <Textarea
                    id="customer-notes"
                    name="notes"
                    value={orderDetails.notes}
                    onChange={handleChange}
                    placeholder={tipoEntrega === 'ventanilla' ? 'Instrucciones especiales' : 'Instrucciones especiales para la entrega'}
                    rows={2}                    className="mt-2 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-lg py-6 shadow-lg shadow-red-900/50"
                >
                  {isSubmitting ? 'Procesando...' : 'Confirmar Pedido'}
                </Button>
              </form>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};